import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Sparkles, Upload, Image as ImageIcon, Search,
  Save, X, Star, Layers, ArrowUp, ArrowDown,
} from "lucide-react";
import {
  adminListLibraryMaterials,
  adminCreateLibraryMaterial,
  adminUpdateLibraryMaterial,
  adminDeleteLibraryMaterial,
  adminUploadLibraryImage,
  adminGenerateLibraryImage,
  adminDeleteLibraryImage,
  adminPatchLibraryImage,
  adminReorderLibraryImages,
} from "../../../lib/library";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Editor — used both for "new" and "edit" via a side drawer. */
const MaterialEditor = ({ open, material, categories, onClose, onSaved }) => {
  const isNew = !material?.id;
  const [form, setForm] = useState(() => ({
    slug: "",
    label: "",
    product_categories: [],
    feels_like: "",
    best_for: "",
    tagline: "",
    spec_pairs: [],
    starting_price: "",
    starting_price_qty: "",
    display_order: 100,
    is_active: true,
    notes: "",
  }));
  const [saving, setSaving] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiCaption, setAiCaption] = useState("");
  const [uploadCaption, setUploadCaption] = useState("");

  useEffect(() => {
    if (open && material) {
      setForm({
        slug: material.slug || "",
        label: material.label || "",
        product_categories: material.product_categories || [],
        feels_like: material.feels_like || "",
        best_for: material.best_for || "",
        tagline: material.tagline || "",
        spec_pairs: material.spec_pairs || [],
        starting_price: material.starting_price ?? "",
        starting_price_qty: material.starting_price_qty ?? "",
        display_order: material.display_order ?? 100,
        is_active: material.is_active !== false,
        notes: material.notes || "",
      });
    } else if (open && !material) {
      setForm({
        slug: "", label: "", product_categories: [],
        feels_like: "", best_for: "", tagline: "",
        spec_pairs: [], starting_price: "", starting_price_qty: "",
        display_order: 100, is_active: true, notes: "",
      });
    }
    setAiPrompt("");
    setAiCaption("");
    setUploadCaption("");
  }, [open, material]);

  if (!open) return null;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleCategory = (c) => {
    setForm((p) => ({
      ...p,
      product_categories: p.product_categories.includes(c)
        ? p.product_categories.filter((x) => x !== c)
        : [...p.product_categories, c],
    }));
  };
  const setSpec = (i, key, value) =>
    setForm((p) => ({
      ...p,
      spec_pairs: p.spec_pairs.map((sp, idx) => (idx === i ? { ...sp, [key]: value } : sp)),
    }));
  const addSpec = () => setForm((p) => ({ ...p, spec_pairs: [...p.spec_pairs, { key: "", value: "" }] }));
  const removeSpec = (i) =>
    setForm((p) => ({ ...p, spec_pairs: p.spec_pairs.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.slug.trim() || !form.label.trim()) {
      toast.error("Slug and Label are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        starting_price: form.starting_price === "" ? null : Number(form.starting_price),
        starting_price_qty: form.starting_price_qty === "" ? null : Number(form.starting_price_qty),
        spec_pairs: form.spec_pairs.filter((sp) => sp.key.trim() || sp.value.trim()),
      };
      const result = isNew
        ? await adminCreateLibraryMaterial(body)
        : await adminUpdateLibraryMaterial(material.id, body);
      toast.success(isNew ? "Material created" : "Saved");
      onSaved(result);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generateAI = async () => {
    if (isNew) {
      toast.error("Save the material first, then generate the photo.");
      return;
    }
    setGenBusy(true);
    try {
      const result = await adminGenerateLibraryImage(material.id, {
        prompt: aiPrompt.trim() || undefined,
        caption: aiCaption.trim() || undefined,
      });
      toast.success("AI photo generated");
      onSaved(result);
      setAiPrompt("");
      setAiCaption("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (isNew) {
      toast.error("Save the material first, then upload a photo.");
      return;
    }
    if (!file) return;
    setUploadBusy(true);
    try {
      const result = await adminUploadLibraryImage(material.id, file, {
        caption: uploadCaption.trim() || undefined,
      });
      toast.success("Photo uploaded");
      onSaved(result);
      setUploadCaption("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
    }
  };

  const removeImage = async (idx) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      const result = await adminDeleteLibraryImage(material.id, idx);
      toast.success("Photo removed");
      onSaved(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const moveImage = async (idx, dir) => {
    const images = material?.images || [];
    const next = idx + dir;
    if (next < 0 || next >= images.length) return;
    const order = images.map((_, i) => i);
    [order[idx], order[next]] = [order[next], order[idx]];
    try {
      const result = await adminReorderLibraryImages(material.id, order);
      onSaved(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  const setImageCaption = async (idx, caption) => {
    try {
      const result = await adminPatchLibraryImage(material.id, idx, { caption: caption || null });
      onSaved(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption save failed");
    }
  };

  const currentImg = material?.image_url ? `${BACKEND}${material.image_url}&ts=${encodeURIComponent(material.updated_at || "")}` : null;
  // currentImg kept available for potential future header preview, but the gallery editor below replaces the single-photo block.
  void currentImg;

  return (
    <div className="fixed inset-0 z-50" data-testid="library-editor">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[640px] bg-white overflow-auto"
        style={{ borderLeft: "3px solid var(--ups-ink)" }}
      >
        <div
          className="sticky top-0 z-10 px-5 py-3 flex items-center justify-between"
          style={{ background: "var(--ups-ink)", color: "#FFB500" }}
        >
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">
              {isNew ? "Create" : "Edit"} · Library
            </div>
            <div className="font-display text-base font-bold">
              {isNew ? "New material" : form.label || "Untitled"}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-sm" aria-label="Close" data-testid="library-editor-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Gallery editor — multi-photo */}
          <div
            className="rounded-sm p-3 space-y-3"
            style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
            data-testid="library-gallery-editor"
          >
            <div className="overline flex items-center gap-1.5">
              <ImageIcon size={11} /> Photo gallery{" "}
              <span className="font-mono text-[10px] text-[color:var(--ups-subtext)] normal-case">
                ({(material?.images || []).length} photo{(material?.images || []).length === 1 ? "" : "s"})
              </span>
            </div>

            {/* Current gallery — one row per photo with thumbnail, caption,
                reorder, delete. Hidden entirely if material is brand-new. */}
            {!isNew && (material?.images || []).length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {material.images.map((img, idx) => {
                  const src = `${BACKEND}${img.url}&ts=${encodeURIComponent(material.updated_at || "")}`;
                  return (
                    <div
                      key={idx}
                      className="flex gap-2 p-2 rounded-sm bg-white"
                      style={{ border: "1px solid var(--ups-border)" }}
                      data-testid={`library-gallery-row-${idx}`}
                    >
                      <img
                        src={src}
                        alt={img.caption || form.label}
                        className="w-16 h-16 object-cover shrink-0 rounded-sm"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0 flex flex-col gap-1">
                        <div className="text-[10px] font-mono text-[color:var(--ups-subtext)]">
                          Photo {idx + 1}{idx === 0 ? " · primary" : ""}
                        </div>
                        <input
                          type="text"
                          defaultValue={img.caption || ""}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== (img.caption || "")) setImageCaption(idx, v);
                          }}
                          placeholder="Caption (e.g. 'thickness comparison')"
                          className="input-swiss px-1.5 py-1 rounded-sm text-[11px] w-full"
                          data-testid={`library-gallery-caption-${idx}`}
                        />
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveImage(idx, -1)}
                            disabled={idx === 0}
                            className="p-1 hover:bg-[color:var(--ups-muted)] rounded-sm disabled:opacity-30"
                            aria-label="Move up"
                            title="Move up"
                          >
                            <ArrowUp size={11} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveImage(idx, +1)}
                            disabled={idx === material.images.length - 1}
                            className="p-1 hover:bg-[color:var(--ups-muted)] rounded-sm disabled:opacity-30"
                            aria-label="Move down"
                            title="Move down"
                          >
                            <ArrowDown size={11} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="p-1 ml-auto text-stone-400 hover:bg-red-50 hover:text-red-700 rounded-sm"
                            aria-label="Remove photo"
                            data-testid={`library-gallery-remove-${idx}`}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : !isNew ? (
              <div className="text-[11px] italic text-stone-500 py-3 text-center">
                No photos yet — upload or generate one below.
              </div>
            ) : (
              <div className="text-[11px] italic text-stone-500 py-3 text-center">
                Save the material first, then add photos.
              </div>
            )}

            {/* Add a NEW photo (upload or generate). Both APPEND, so existing
                photos stay put — no accidental overwrites. */}
            {!isNew && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t" style={{ borderColor: "var(--ups-border)" }}>
                <div className="flex flex-col gap-1.5">
                  <div className="overline text-[10px]">Add by upload</div>
                  <input
                    value={uploadCaption}
                    onChange={(e) => setUploadCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    className="input-swiss px-2 py-1.5 rounded-sm text-xs"
                    data-testid="library-upload-caption"
                  />
                  <label
                    className="btn-ink px-3 py-2 rounded-sm text-xs flex items-center gap-2 cursor-pointer justify-center"
                    data-testid="library-upload-label"
                  >
                    {uploadBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    Choose file
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }}
                      data-testid="library-upload-input"
                    />
                  </label>
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="overline text-[10px]">Add via AI</div>
                  <input
                    value={aiCaption}
                    onChange={(e) => setAiCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    className="input-swiss px-2 py-1.5 rounded-sm text-xs"
                    data-testid="library-ai-caption"
                  />
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Custom prompt (leave blank for default)"
                    rows={2}
                    className="input-swiss text-xs p-2 rounded-sm font-mono"
                    data-testid="library-ai-prompt"
                  />
                  <button
                    type="button"
                    onClick={generateAI}
                    disabled={genBusy}
                    className="btn-brutal px-3 py-2 rounded-sm text-xs flex items-center gap-2 justify-center"
                    data-testid="library-generate-ai"
                  >
                    {genBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Generate AI photo
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Core fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Slug *</div>
              <input
                value={form.slug}
                onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))}
                placeholder="16pt-gloss-cover"
                className="input-swiss w-full px-2 py-1.5 rounded-sm font-mono text-sm"
                data-testid="library-field-slug"
              />
            </div>
            <div>
              <div className="overline mb-1">Label *</div>
              <input
                value={form.label}
                onChange={(e) => setField("label", e.target.value)}
                placeholder="16pt Gloss Cover"
                className="input-swiss w-full px-2 py-1.5 rounded-sm text-sm"
                data-testid="library-field-label"
              />
            </div>
            <div className="sm:col-span-2">
              <div className="overline mb-1">Product categories</div>
              <div className="flex flex-wrap gap-1.5">
                {categories.map((c) => {
                  const on = form.product_categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCategory(c)}
                      className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm border-2 transition"
                      style={{
                        background: on ? "var(--ups-ink)" : "transparent",
                        color: on ? "#FFB500" : "var(--ups-ink)",
                        borderColor: on ? "var(--ups-ink)" : "var(--ups-border)",
                      }}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="overline mb-1">Tagline (short pill)</div>
              <input
                value={form.tagline}
                onChange={(e) => setField("tagline", e.target.value)}
                placeholder='e.g. "Most popular" or "Premium"'
                className="input-swiss w-full px-2 py-1.5 rounded-sm text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <div className="overline mb-1">Feels like</div>
              <input
                value={form.feels_like}
                onChange={(e) => setField("feels_like", e.target.value)}
                placeholder="a thick playing card, slick to touch"
                className="input-swiss w-full px-2 py-1.5 rounded-sm text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <div className="overline mb-1">Best for</div>
              <input
                value={form.best_for}
                onChange={(e) => setField("best_for", e.target.value)}
                placeholder="ads, photo postcards, real-estate"
                className="input-swiss w-full px-2 py-1.5 rounded-sm text-sm"
              />
            </div>
          </div>

          {/* Specs */}
          <div>
            <div className="overline mb-2 flex items-center justify-between">
              <span>Spec pairs</span>
              <button
                type="button"
                onClick={addSpec}
                className="text-[10px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 hover:underline"
                data-testid="library-add-spec"
              >
                <Plus size={11} /> Add
              </button>
            </div>
            <div className="space-y-1.5">
              {form.spec_pairs.map((sp, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.5fr_auto] gap-1.5">
                  <input
                    value={sp.key}
                    onChange={(e) => setSpec(i, "key", e.target.value)}
                    placeholder="Thickness"
                    className="input-swiss px-2 py-1.5 rounded-sm text-xs font-mono"
                  />
                  <input
                    value={sp.value}
                    onChange={(e) => setSpec(i, "value", e.target.value)}
                    placeholder='16pt (.016")'
                    className="input-swiss px-2 py-1.5 rounded-sm text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => removeSpec(i)}
                    className="px-2 py-1.5 text-stone-500 hover:bg-red-50 hover:text-red-700 rounded-sm"
                    aria-label="Remove spec"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {form.spec_pairs.length === 0 && (
                <div className="text-[11px] text-stone-400 italic">No specs yet — add Thickness, Finish, Outdoor life, etc.</div>
              )}
            </div>
          </div>

          {/* Display order */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Display order</div>
              <input
                type="number"
                value={form.display_order}
                onChange={(e) => setField("display_order", Number(e.target.value) || 0)}
                className="input-swiss w-full px-2 py-1.5 rounded-sm text-sm font-mono"
              />
            </div>
            <label className="flex items-center gap-2 text-sm self-end pb-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setField("is_active", e.target.checked)}
                data-testid="library-field-active"
              />
              <span className="font-bold uppercase text-[11px] tracking-[0.12em]">Visible to customers</span>
            </label>
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="w-full btn-brutal px-4 py-3 rounded-sm font-bold uppercase tracking-[0.16em] flex items-center justify-center gap-2"
            data-testid="library-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isNew ? "Create material" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const MaterialLibraryTab = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const d = await adminListLibraryMaterials();
      setItems(d.items || []);
      setCategories(d.categories || []);
    } catch {
      toast.error("Could not load materials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) =>
      `${m.label} ${m.slug} ${(m.product_categories || []).join(" ")} ${m.tagline || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [items, query]);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (m) => { setEditing(m); setEditorOpen(true); };

  const onSaved = (updated) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === updated.id);
      if (idx === -1) return [...prev, updated];
      const next = prev.slice();
      next[idx] = updated;
      return next;
    });
    setEditing(updated);
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Delete "${m.label}"? This removes the photo too.`)) return;
    try {
      await adminDeleteLibraryMaterial(m.id);
      toast.success("Deleted");
      setItems((prev) => prev.filter((p) => p.id !== m.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const missingPhoto = items.filter((m) => !m.image_key).length;

  return (
    <div className="space-y-4" data-testid="library-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><Layers size={20} /> Material Library</h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            Customer-facing catalog at <a href="/materials" target="_blank" rel="noopener noreferrer" className="underline font-bold">/materials</a>. Edit photos, descriptions, and pricing. AI bootstraps placeholder photos via Nano Banana.
          </p>
        </div>
        <button onClick={openNew} className="btn-brutal px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" data-testid="library-new-btn">
          <Plus size={12} /> New material
        </button>
      </div>

      {missingPhoto > 0 && (
        <div className="px-3 py-2 rounded-sm text-xs flex items-center gap-2" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" }}>
          <Sparkles size={12} /> {missingPhoto} material{missingPhoto === 1 ? "" : "s"} still need a photo — click any row and hit "Generate AI photo".
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, category…"
            className="input-swiss w-full pl-7 pr-2 py-1.5 rounded-sm text-xs"
            data-testid="library-search"
          />
        </div>
        <div className="text-[11px] font-mono text-[color:var(--ups-subtext)]">{filtered.length} of {items.length}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="library-grid">
        {loading ? (
          <div className="col-span-full flex items-center gap-2 text-sm text-stone-500">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full text-sm text-stone-500">No materials match.</div>
        ) : filtered.map((m) => {
          const img = m.image_url ? `${BACKEND}${m.image_url}&ts=${encodeURIComponent(m.updated_at || "")}` : null;
          const galleryCount = (m.images || []).length;
          return (
            <div
              key={m.id}
              className="rounded-sm overflow-hidden flex"
              style={{ background: "white", border: "1px solid var(--ups-border)" }}
              data-testid={`library-row-${m.slug}`}
            >
              <button
                type="button"
                onClick={() => openEdit(m)}
                className="w-28 h-28 shrink-0 flex items-center justify-center relative"
                style={{ background: "var(--ups-muted)", borderRight: "1px solid var(--ups-border)" }}
              >
                {img ? (
                  <img src={img} alt={m.label} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <ImageIcon size={20} className="text-stone-300" />
                )}
                {galleryCount > 1 && (
                  <span
                    className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] rounded-sm"
                    style={{ background: "rgba(28,25,23,0.85)", color: "#FFB500" }}
                    data-testid={`library-row-photo-count-${m.slug}`}
                  >
                    +{galleryCount - 1}
                  </span>
                )}
              </button>
              <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(m)}
                      className="text-left font-display font-bold text-sm leading-tight hover:underline truncate flex-1 min-w-0"
                    >
                      {m.label}
                    </button>
                    {m.tagline && (
                      <span
                        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] rounded-sm whitespace-nowrap inline-flex items-center gap-0.5 shrink-0"
                        style={{ background: /popular|best/i.test(m.tagline) ? "#FFB500" : "#1C1917", color: /popular|best/i.test(m.tagline) ? "#1C1917" : "#FFB500" }}
                      >
                        {/popular|best/i.test(m.tagline) && <Star size={9} fill="currentColor" />} {m.tagline}
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-[color:var(--ups-subtext)] truncate">{m.slug}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.1em] font-bold text-[color:var(--ups-subtext)] truncate">
                    {(m.product_categories || []).join(" · ") || "no category"}
                  </div>
                </div>
                <div className="flex items-center justify-end mt-2">
                  <div className="flex gap-1">
                    {!m.is_active && (
                      <span className="text-[9px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm" style={{ background: "#FEE2E2", color: "#991B1B" }}>
                        Hidden
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => onDelete(m)}
                      className="p-1 text-stone-400 hover:text-red-600 rounded-sm"
                      aria-label="Delete"
                      data-testid={`library-delete-${m.slug}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <MaterialEditor
        open={editorOpen}
        material={editing}
        categories={categories}
        onClose={() => setEditorOpen(false)}
        onSaved={onSaved}
      />
    </div>
  );
};

export default MaterialLibraryTab;
